import React, { useCallback, useEffect, useRef, useState } from "react";
import { haptic } from "@/lib/haptic";

export interface CycleOption<T> {
  label: string;
  value: T;
  color: string;
}

interface CycleButtonProps<T> {
  options: CycleOption<T>[];
  /** Controlled current index. */
  index: number;
  loop?: boolean;
  haptics?: boolean;
  icon?: React.ReactNode;
  /** Advance forward; long-press / right-click goes backward. */
  onChange?: (option: CycleOption<T>) => void;
  ariaLabel?: string;
}

/**
 * Generic tap-to-cycle pill (PWA port of ui/atoms/button/CycleButton).
 * Tap = advance to next option (loops); long-press or right-click = reverse.
 * Background = the current option's color.
 */
export default function CycleButton<T>({
  options,
  index,
  loop = true,
  haptics = true,
  icon,
  onChange,
  ariaLabel,
}: CycleButtonProps<T>) {
  const safeIndex = Math.min(Math.max(index, 0), Math.max(options.length - 1, 0));
  const current = options[safeIndex] ?? options[0];
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  const [pressing, setPressing] = useState(false);

  const step = useCallback(
    (dir: 1 | -1) => {
      if (options.length <= 1) return;
      if (haptics) haptic.light();
      let next = safeIndex + dir;
      if (next >= options.length) next = loop ? 0 : options.length - 1;
      if (next < 0) next = loop ? options.length - 1 : 0;
      onChange?.(options[next]);
    },
    [options, safeIndex, loop, haptics, onChange]
  );

  const handleClick = useCallback(() => {
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    step(1);
  }, [step]);

  const startLongPress = useCallback(() => {
    setPressing(true);
    longPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressed.current = true;
      step(-1);
    }, 450);
  }, [step]);

  const cancelLongPress = useCallback(() => {
    setPressing(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  if (!current) return null;

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? current.label}
      onClick={handleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        step(-1);
      }}
      onPointerDown={startLongPress}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      className="flex flex-row items-center justify-center gap-1.5 min-w-[120px] w-auto rounded-xl p-2 shadow-md select-none transition-transform"
      style={{
        backgroundColor: current.color,
        transform: pressing ? "scale(0.97)" : "scale(1)",
      }}
    >
      {icon}
      <span className="text-white font-bold text-sm whitespace-nowrap">
        {current.label}
      </span>
    </button>
  );
}
